/* ============================================================
   局外部件（M10）
   ============================================================

   六個部位，每個部位多個可購買的部件。買下來永久擁有、可自由切換，
   買下來之後再用廢料逐級升（最高 5 級）。設計說明見 GDD §11.2。

   **這是支柱五（「機體是玩家組出來的」）的載體**，不是附加系統。
   三條判準寫在支柱裡，這個檔案的內容要一直對照它：

     1. 每個部件都要看得出來（`shape` 欄位 → M11 接上外形）
     2. 部件之間是**取捨不是階梯**（同部位不能有一個嚴格最強）
     3. 組合要能改變打法，不只是改變數值

   **每個部位的起手部件（`starter`）在 Lv1 沒有任何加成。**
   這是刻意的：它讓「局外等級 0」有一個乾淨的定義 ——
   全部起手件 Lv1 = 新玩家的第一場，也是所有校準測試的基準（GDD §11.4）。
   起手件從 Lv2 起才給加成，所以它不是廢物，只是基準線。 */

export type PartMods = { mul?: Record<string, number>, add?: Record<string, number> };

export type Part = {
  id: string; slot: string; n: string; starter?: boolean;
  /* **`shape` 一律等於 `id`。** 兩者原本是不同的命名空間（例如 id `core-burn`
     配 shape `core-vent`），而有些部件剛好兩邊同名 —— 那種「有時一樣有時不一樣」
     的設計害我自己在寫測試時把形狀代號當成 id 傳進 `buyPart`，
     函式回 false 而測試的錯誤訊息完全看不出原因。
     保留 `shape` 這個欄位是為了「外形」在概念上仍是獨立的一件事，
     但值一律跟 id 相同，讓歧義消失。有測試守這條。 */
  d: (lv: number) => string;
  cost: number;                       // 購買價（廢料）
  shape: string;                      // 外形代號，M11 用
  mods?: (lv: number) => PartMods;    // 進修正層（與被動同一層，順序無關）
  /** 不在 `P.s` 裡的效果：結構上限、復活、槽位、升級選項數、主武裝改裝。 */
  grant?: (lv: number) => { hp?: number, sh?: number, revives?: number, dashCut?: number,
                            wSlot?: number, pSlot?: number, choices?: number,
                            primaryMod?: string, freeLv?: number };
};

/* ── 主武裝位在不同機體上是不同的東西 ────────────────────────
   MAIN 這一位掛的是**整條右臂**（`entities/mech-arm.ts` / `lancer-arm.ts`），
   所以在 CHASSIS-0 上它是砲、在 LANCER-2 上它是刃。同一顆部件必須：
     ① 叫對名字（不然近戰玩家在機庫看到「標準機炮」會以為裝錯）
     ② 給對改裝（`primaryMod` 是**按武器 key 註冊**的，`data/modules.ts`
        的 `pierce`/`chain` 只吃 vulcan —— 直接沿用的話近戰玩家花 160 廢料
        買到的是一個永遠不生效的模組，而且沒有任何錯誤訊息）
   對應關係按語意配：貫穿→擴幅（都是「打得到更多」）、跳彈→衝擊波（都是
   「一次命中變多次」）。`mechUpName()` 解的是同一類問題，作法刻意一致。 */
export var MAIN_ALT: any = {
  blade: {
    'main-vulcan': {
      n: '標準電磁刃',
      d: function (lv) { return lv < 2 ? '基準規格，無加成' : '主武裝揮擊速度 +' + ((lv - 1) * 8) + '%'; }
    },
    'main-pierce': {
      n: '穿刺長刃', mod: 'wide',
      d: function (lv) { return '斬擊範圍 +45% 並附帶擊退；主武裝傷害 +' + (lv * 5) + '%'; }
    },
    'main-chain': {
      n: '鏈鋸刃', mod: 'pulse',
      d: function (lv) { return '每次斬擊射出穿透斬波；主武裝揮擊速度 +' + (lv * 5) + '%'; }
    }
  },
  /* TITAN-3：MAIN 這一位是**整套榴彈砲**。對應關係同樣按語意配 ——
     貫穿→長管（打得更遠更重）、跳彈→散射（一次命中變多次）。
     `mod` 的代號要是 `sim/weapons-titan.ts` 的 `fireMortar` 認得的，
     直接沿用 vulcan 的 `pierce`/`chain` 會買到一個永遠不生效的模組。 */
  mortar: {
    'main-vulcan': {
      n: '標準榴彈砲',
      d: function (lv) { return lv < 2 ? '基準規格，無加成' : '主武裝射速 +' + ((lv - 1) * 8) + '%'; }
    },
    'main-pierce': {
      n: '長管榴彈砲', mod: 'heavy',
      d: function (lv) { return '單發傷害 +25%、爆炸範圍 +40%；主武裝傷害 +' + (lv * 5) + '%'; }
    },
    'main-chain': {
      n: '散射榴彈砲', mod: 'salvo',
      d: function (lv) { return '每次開火多一發、落點岔開（各 80% 傷害）；主武裝射速 +' + (lv * 5) + '%'; }
    }
  }
};
/** 這顆部件在「主武裝是 `primary` 的機體」上叫什麼。 */
export function partName(part, primary) {
  var a = MAIN_ALT[primary] && MAIN_ALT[primary][part.id];
  return (a && a.n) || part.n;
}
/** 同上，說明文字。 */
export function partDesc(part, lv, primary) {
  var a = MAIN_ALT[primary] && MAIN_ALT[primary][part.id];
  return a && a.d ? a.d(lv) : part.d(lv);
}
/** 同上，`grant.primaryMod` 的實際值（換成該武器認得的改裝代號）。 */
export function partMod(part, primary) {
  var a = MAIN_ALT[primary] && MAIN_ALT[primary][part.id];
  if (a) return a.mod || null;
  var g = part.grant ? part.grant(1) : null;
  return (g && g.primaryMod) || null;
}

/** 升到第 lv 級要花多少廢料（lv 是「升上去之後」的等級）。 */
export function upCost(lv) { return 30 + (lv - 1) * 35; }

export var PARTS: Part[] = [
  /* ---------- 核心 CORE：打得動 ---------- */
  {
    id: 'core-std', slot: 'core', n: '標準核心', starter: true, cost: 0, shape: 'core-std',
    d: function (lv) { return lv < 2 ? '基準規格，無加成' : '全武裝傷害 +' + ((lv - 1) * 2) + '%'; },
    mods: function (lv) { return { mul: { atkDmg: 1 + (lv - 1) * 0.02, midDmg: 1 + (lv - 1) * 0.02, ultDmg: 1 + (lv - 1) * 0.02 } }; }
  },
  {
    id: 'core-crit', slot: 'core', n: '臨界核心', cost: 120, shape: 'core-crit',
    d: function (lv) { return '暴擊率 +' + (lv * 3) + '%、暴擊傷害 +' + (lv * 8) + '%'; },
    mods: function (lv) { return { add: { crit: lv * 0.03, critMul: lv * 0.08 } }; }
  },
  {
    id: 'core-burn', slot: 'core', n: '過載核心', cost: 150, shape: 'core-burn',
    d: function (lv) { return '全武裝傷害 +' + (lv * 2) + '%，但結構上限 −' + (lv * 7) + ''; },
    mods: function (lv) { return { mul: { atkDmg: 1 + lv * 0.02, midDmg: 1 + lv * 0.02, ultDmg: 1 + lv * 0.02 } }; },
    grant: function (lv) { return { hp: -lv * 7 }; }
  },

  /* ---------- 駕艙 COCKPIT：活得久 ---------- */
  {
    id: 'cock-std', slot: 'cockpit', n: '標準駕艙', starter: true, cost: 0, shape: 'cock-std',
    d: function (lv) { return lv < 2 ? '基準規格，無加成' : '結構上限 +' + ((lv - 1) * 3); },
    grant: function (lv) { return { hp: (lv - 1) * 3 }; }
  },
  {
    id: 'cock-armor', slot: 'cockpit', n: '重裝駕艙', cost: 130, shape: 'cock-armor',
    d: function (lv) { return '結構上限 +' + (lv * 3) + '，但移速 −' + (lv * 3) + '%'; },
    mods: function (lv) { return { mul: { speed: Math.pow(0.97, lv) } }; },
    grant: function (lv) { return { hp: lv * 3 }; }
  },
  {
    id: 'cock-esc', slot: 'cockpit', n: '逃生駕艙', cost: 170, shape: 'cock-esc',
    d: function (lv) { return '護盾上限 +' + (lv * 2) + (lv >= 3 ? '、起始復活 +1' : ''); },
    grant: function (lv) { return { sh: lv * 2, revives: lv >= 3 ? 1 : 0 }; }
  },

  /* ---------- 推進器 THRUSTER：閃得掉 ---------- */
  {
    id: 'thr-twin', slot: 'thruster', n: '雙推進', starter: true, cost: 0, shape: 'thr-twin',
    d: function (lv) { return lv < 2 ? '基準規格，無加成' : '移動速度 +' + ((lv - 1) * 1.5).toFixed(1) + '%'; },
    mods: function (lv) { return { mul: { speed: 1 + (lv - 1) * 0.015 } }; }
  },
  {
    /* 環形＝容量：局外部件也吃推進能量（新的軸要有人動它，否則是死碼）。
       這裡刻意不新增部件 —— 新部件要新造型，而造型是移植管線管的。 */
    id: 'thr-ring', slot: 'thruster', n: '環形推進', cost: 140, shape: 'thr-ring',
    /* Lv3 的加成從「衝刺充能 +1」改成「衝刺耗能 −20%」（2026-08-06）：
       衝刺已經沒有充能格了，它跟飛行共用推進能量。 */
    d: function (lv) { return '移速 +' + (lv * 1.4).toFixed(1) + '%、推進能量 +' + (lv * 6) + '%' + (lv >= 3 ? '、衝刺耗能 −20%' : ''); },
    mods: function (lv) { return { mul: { speed: 1 + lv * 0.014, thrustMax: 1 + lv * 0.06 } }; },
    grant: function (lv) { return { dashCut: lv >= 3 ? 0.2 : 0 }; }
  },
  {
    id: 'thr-heavy', slot: 'thruster', n: '重載腳架', cost: 110, shape: 'thr-heavy',
    d: function (lv) { return '結構上限 +' + (lv * 2) + '、落地衝擊 +' + (lv * 12) + '%，但移速 −' + (lv * 2) + '%、爬升 −' + (lv * 4) + '%'; },
    mods: function (lv) { return { mul: { speed: Math.pow(0.98, lv), slamDmg: 1 + lv * 0.12, thrustRise: Math.pow(0.96, lv) } }; },
    grant: function (lv) { return { hp: lv * 2 }; }
  },

  /* ---------- 主武器 MAIN：開局不空窗 ---------- */
  {
    id: 'main-vulcan', slot: 'main', n: '標準機炮', starter: true, cost: 0, shape: 'main-vulcan',
    d: function (lv) { return lv < 2 ? '基準規格，無加成' : '主武裝射速 +' + ((lv - 1) * 1) + '%'; },
    mods: function (lv) { return { mul: { atkRate: 1 + (lv - 1) * 0.01 } }; }
  },
  {
    id: 'main-pierce', slot: 'main', n: '貫穿彈芯', cost: 160, shape: 'main-pierce',
    d: function (lv) { return '子彈穿透 3 個目標；主武裝傷害 +' + (lv * 0.8).toFixed(1) + '%'; },
    mods: function (lv) { return { mul: { atkDmg: 1 + lv * 0.008 } }; },
    grant: function () { return { primaryMod: 'pierce' }; }
  },
  {
    id: 'main-chain', slot: 'main', n: '跳彈迴路', cost: 160, shape: 'main-chain',
    d: function (lv) { return '命中後彈向下一個目標；主武裝射速 +' + (lv * 0.8).toFixed(1) + '%'; },
    mods: function (lv) { return { mul: { atkRate: 1 + lv * 0.008 } }; },
    grant: function () { return { primaryMod: 'chain' }; }
  },

  /* ---------- 感應器 SENSOR：成長得快 ---------- */
  {
    id: 'sen-std', slot: 'sensor', n: '標準感應器', starter: true, cost: 0, shape: 'sen-std',
    d: function (lv) { return lv < 2 ? '基準規格，無加成' : '經驗吸取範圍 +' + ((lv - 1) * 20) + '%'; },
    mods: function (lv) { return { mul: { magnet: 1 + (lv - 1) * 0.20 } }; }
  },
  {
    id: 'sen-wide', slot: 'sensor', n: '廣域感應', cost: 100, shape: 'sen-wide',
    d: function (lv) { return '經驗吸取範圍 +' + (lv * 45) + '%'; },
    mods: function (lv) { return { mul: { magnet: 1 + lv * 0.45 } }; }
  },
  {
    /* 05-meta-progression.md 點名「拾取範圍 +40%、幸運 +20%」是把局外預算
       花在**體感強但實際弱**的屬性上的範例 —— 它們讓新手感覺得到成長，
       又不會把永久強化的總戰力增益推過 30% 的硬上限。
       放在感測器位是因為這一位現有的東西（吸取範圍、四選一）本來就都是
       「改變你拿到什麼」而不是「改變你打得多痛」。 */
    id: 'sen-luck', slot: 'sensor', n: '機率演算單元', cost: 160, shape: 'sen-luck',
    d: function (lv) { return '幸運 +' + (lv * 20) + '%（寶箱開出多件的機率）'; },
    mods: function (lv) { return { mul: { luck: 1 + lv * 0.20 } }; }
  },
  {
    id: 'sen-tac', slot: 'sensor', n: '戰術感應', cost: 200, shape: 'sen-tac',
    d: function (lv) {
      return lv < 3 ? '吸取範圍 +' + (lv * 12) + '%（3 級起升級改為四選一）'
        : '升級改為**四選一**、吸取範圍 +' + (lv * 12) + '%';
    },
    mods: function (lv) { return { mul: { magnet: 1 + lv * 0.12 } }; },
    grant: function (lv) { return { choices: lv >= 3 ? 1 : 0 }; }
  },

  /* ---------- 掛架 PYLON：build 空間 ---------- */
  {
    id: 'pyl-std', slot: 'pylon', n: '標準掛架', starter: true, cost: 0, shape: 'pyl-std',
    d: function (lv) { return lv < 4 ? '（4 級起開局多 1 次升級）' : '開局多 1 次升級'; },
    grant: function (lv) { return { freeLv: lv >= 4 ? 1 : 0 }; }
  },
  {
    id: 'pyl-weapon', slot: 'pylon', n: '武器擴充架', cost: 180, shape: 'pyl-weapon',
    d: function (lv) { return lv < 3 ? '（3 級起武器槽 +1）' : '武器槽 +1'; },
    grant: function (lv) { return { wSlot: lv >= 3 ? 1 : 0 }; }
  },
  {
    id: 'pyl-passive', slot: 'pylon', n: '被動擴充架', cost: 180, shape: 'pyl-passive',
    d: function (lv) { return lv < 3 ? '（3 級起被動槽 +1）' : '被動槽 +1'; },
    grant: function (lv) { return { pSlot: lv >= 3 ? 1 : 0 }; }
  }
];

export var SLOTS = [
  { id: 'core', n: '核心', en: 'CORE', hint: '打得動' },
  { id: 'cockpit', n: '駕艙', en: 'COCKPIT', hint: '活得久' },
  { id: 'thruster', n: '推進器', en: 'THRUSTER', hint: '閃得掉' },
  { id: 'main', n: '主武器', en: 'MAIN', hint: '開局不空窗' },
  { id: 'sensor', n: '感應器', en: 'SENSOR', hint: '成長得快' },
  { id: 'pylon', n: '掛架', en: 'PYLON', hint: 'build 空間' }
];

export function partById(id) {
  for (var i = 0; i < PARTS.length; i++) if (PARTS[i].id === id) return PARTS[i];
  return null;
}
export function partsOfSlot(slot) {
  return PARTS.filter(function (p) { return p.slot === slot; });
}
export function starterOf(slot) {
  var f = PARTS.filter(function (p) { return p.slot === slot && p.starter; });
  return f.length ? f[0] : null;
}
